import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Base cart item type
export interface CartItem {
  id: string;
  name: string;
  price_amount: number;
  price_currency: string;
  image_url?: string;
  sku: string;
  quantity: number;
  port_code?: string; // Optional port_code for multi-port devices
  tax_rate_pct?: number; // Tax rate percentage (default 25%)
  availableStock?: number; // Available stock at time of adding to cart
  bundle_id?: string; // Set when this item is part of a bundle purchase
  bundleImages?: string[]; // Product images for bundle display in cart
  bundle_selection?: { product_id: string; quantity: number }[]; // mix & match: the customer's picked products (validated server-side)
}

// Applied promo code info
export interface AppliedPromoCode {
  code: string;
  promo_code_id: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  discount_amount: number; // Calculated discount in öre
  referrer_name?: string | null;
}

interface TradeCartState {
  items: CartItem[];
  deviceId?: string;
  appliedPromoCode?: AppliedPromoCode | null;
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  setDeviceId: (deviceId: string) => void;
  setAppliedPromoCode: (promoCode: AppliedPromoCode | null) => void;
  clearPromoCode: () => void;
  getTotalAmount: () => number;
  getTotalWithDiscount: () => number;
  getTotalItems: () => number;
  getItemQuantity: (itemId: string) => number;
}

export const useTradeCart = create<TradeCartState>()(
  persist(
    (set, get) => ({
      items: [],
      deviceId: undefined,
      appliedPromoCode: null,
      
      addItem: (item, quantity = 1) => {
        // SECURITY: Validate quantity - REJECT negative/zero/excessive values (don't convert)
        const parsedQty = parseInt(String(quantity), 10);
        if (isNaN(parsedQty) || parsedQty < 1 || parsedQty > 99) {
          console.warn('[TradeCart] SECURITY: Invalid quantity REJECTED:', quantity);
          return; // Silently reject invalid quantities
        }
        
        set((state) => {
          const existingItem = state.items.find(i => i.id === item.id);
          const currentInCart = existingItem?.quantity || 0;
          
          // SECURITY: Limit by available stock (if provided)
          const availableStock = item.availableStock ?? 99;
          const maxCanAdd = Math.max(0, availableStock - currentInCart);
          
          if (maxCanAdd === 0) {
            console.warn('[TradeCart] Stock limit reached for product:', item.id);
            return state; // Return unchanged state
          }
          
          // Clamp quantity to what's available
          const qtyToAdd = Math.min(parsedQty, maxCanAdd);
          
          if (existingItem) {
            return {
              ...state,
              items: state.items.map(i =>
                i.id === item.id
                  ? { ...i, quantity: currentInCart + qtyToAdd, availableStock }
                  : i
              ),
              // Clear promo code when cart changes (price may change)
              appliedPromoCode: null
            };
          }
          
          return {
            ...state,
            items: [...state.items, { ...item, quantity: qtyToAdd, availableStock }],
            appliedPromoCode: null
          };
        });
      },
      
      removeItem: (itemId) => {
        set((state) => ({
          ...state,
          items: state.items.filter(item => item.id !== itemId),
          appliedPromoCode: null // Clear promo when cart changes
        }));
      },
      
      updateQuantity: (itemId, quantity) => {
        // SECURITY: Validate quantity - REJECT negative/excessive values
        const parsedQty = parseInt(String(quantity), 10);
        
        // Allow 0 to trigger removal, but reject negative and excessive
        if (isNaN(parsedQty) || parsedQty < 0 || parsedQty > 99) {
          console.warn('[TradeCart] SECURITY: Invalid quantity update REJECTED:', quantity);
          return; // Silently reject invalid quantities
        }
        
        if (parsedQty === 0) {
          get().removeItem(itemId);
          return;
        }
        
        set((state) => {
          const item = state.items.find(i => i.id === itemId);
          if (!item) return state;
          
          // SECURITY: Limit by available stock (if stored on item)
          const maxQty = item.availableStock ?? 99;
          const clampedQty = Math.min(parsedQty, maxQty);
          
          return {
            ...state,
            items: state.items.map(i =>
              i.id === itemId
                ? { ...i, quantity: clampedQty }
                : i
            ),
            appliedPromoCode: null // Clear promo when cart changes
          };
        });
      },
      
      clearCart: () => {
        set({ items: [], deviceId: undefined, appliedPromoCode: null });
      },
      
      setDeviceId: (newDeviceId) => {
        const currentDeviceId = get().deviceId;
        
        // KRITISK SÄKERHET: Om deviceId ändras OCH det finns varor i korgen = potentiellt fel
        // Rensa cart för att förhindra att QR-nyckel genereras för fel enhet
        if (currentDeviceId && newDeviceId !== currentDeviceId && get().items.length > 0) {
          console.warn('[TradeCart] SECURITY: Device changed with items in cart - clearing cart to prevent wrong device QR generation');
          set({ items: [], deviceId: newDeviceId, appliedPromoCode: null });
        } else {
          set({ deviceId: newDeviceId });
        }
      },
      
      setAppliedPromoCode: (promoCode) => {
        set({ appliedPromoCode: promoCode });
      },
      
      clearPromoCode: () => {
        set({ appliedPromoCode: null });
      },
      
      getTotalAmount: () => {
        return get().items.reduce((total, item) => total + (item.price_amount * item.quantity), 0);
      },
      
      getTotalWithDiscount: () => {
        const total = get().getTotalAmount();
        const discount = get().appliedPromoCode?.discount_amount || 0;
        return Math.max(0, total - discount);
      },
      
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },
      
      getItemQuantity: (itemId) => {
        const item = get().items.find(i => i.id === itemId);
        return item?.quantity || 0;
      },
    }),
    {
      name: 'trade-cart-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
